import { useEffect } from "react";
import { useTranslation } from "react-i18next";

/**
 * Confirmation sheet used for destructive / consequential actions. Reuses the
 * existing `tp-sheet` look so it matches the "Send for polish?" sheet — same
 * overlay, same rounded card, same button stack. Closes on overlay click,
 * Escape, or the Cancel button. `busy` keeps both buttons disabled while the
 * confirming action is in flight.
 */
export interface ConfirmDialogProps {
  open: boolean;
  title: string;
  body?: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  destructive?: boolean;
  busy?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

export function ConfirmDialog({
  open,
  title,
  body,
  confirmLabel,
  cancelLabel,
  destructive,
  busy,
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  const { t } = useTranslation();
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !busy) onCancel();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, busy, onCancel]);

  if (!open) return null;

  return (
    <div
      className="tp-sheet-overlay"
      onClick={() => {
        if (!busy) onCancel();
      }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="tp-confirm-title"
    >
      <div className="tp-sheet" onClick={(e) => e.stopPropagation()}>
        <div style={{ display: "flex", justifyContent: "center", marginBottom: 14 }}>
          <div style={{
            width: 40, height: 4, borderRadius: 999,
            background: "var(--tp-ink-soft)", opacity: 0.3,
          }} />
        </div>
        <div id="tp-confirm-title" className="tp-h1" style={{ marginBottom: 8 }}>
          {title}
        </div>
        {body && (
          <div
            className="tp-body"
            style={{ color: "var(--tp-ink-soft)", marginBottom: 18, fontSize: 15 }}
          >
            {body}
          </div>
        )}
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <button
            type="button"
            className={destructive ? "tp-btn tp-btn-danger tp-btn-block" : "tp-btn tp-btn-primary tp-btn-block"}
            onClick={onConfirm}
            disabled={busy}
            autoFocus
          >
            {busy ? t("confirmDialog.working") : (confirmLabel ?? t("confirmDialog.confirm"))}
          </button>
          <button
            type="button"
            className="tp-btn tp-btn-block"
            onClick={onCancel}
            disabled={busy}
          >
            {cancelLabel ?? t("common.cancel")}
          </button>
        </div>
      </div>
    </div>
  );
}
